import { Component, OnInit, Inject, Input, Optional, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { Validators, FormBuilder } from '@angular/forms';

import { emailTakenValidator } from '@shared/core/validators/email-taken.shared.validator';
import { cellPhoneCommonValidators } from '@shared/core/validators/cell-phone.shared.validator';
import { passwordPatternValidator } from '@shared/core/validators/password-patterns.shared.validator';
import * as Utils from '@shared/core/utils';
import * as Services from '@shared/core/services';
import * as Tokens from '@shared/core/tokens';
import { Controllers } from '@shared/core/controllers';

import { FormTypesComponent } from '../form-types.shared.component';
import { ILoyaltyAppCountryAssignmentModel, IMemberModel } from '@shared/state';

@Component({
    selector: 'shared-form-for-sign-up',
    templateUrl: './form-for-sign-up.shared.component.html',
    styleUrls: ['./form-for-sign-up.shared.component.scss']
})
export class FormForSignUpComponent extends FormTypesComponent implements OnInit, OnDestroy {
    public countryId: number;
    public cellNo: string;
    public currCountry: ICountries;
    public countries: ILoyaltyAppCountryAssignmentModel[];


    @Input() public data: OLO.Forms.ISingUp;
    @Input() public member: IMemberModel;
    @Input() public cellNumber: string; /* This value is in format countryId:phoneNO, i.e. 1:13423423 */

    constructor(
        @Inject(Tokens.CONFIG_TOKEN) public config: IConfig,
        public formBuilder: FormBuilder,
        public membersService: Services.MembersService,
        public controllers: Controllers,
        @Optional() public formsService: Services.FormsService,
        @Optional() public changeDetectorRef: ChangeDetectorRef,
    ) {
        super(config, formBuilder);

        this.formName = 'sign-up';
    }

    ngOnInit(): void {
        this.loading$ = this.controllers.members.signUpInitStatus$();
        this.currCountry = this.config.localization.country;
        if (!this.cellNumber && this.member && this.member.MobilePhone) {
            this.cellNumber = this.member.MobilePhone;
        }

        this.controllers.appSettings.appGetCounrtries$().subscribe((countries) => {
            this.countries = countries;

            if (this.cellNumber) {
                if (this.cellNumber.charAt(0) !== '+' && this.cellNumber.includes(':')) {
                    this.cellNumber.split(':').forEach((str, index) => {
                        if (!index) {
                            this.countryId = +str;
                        } else {
                            this.cellNo = str;
                        }
                    });
                } else {
                    const mobileExtracted = Utils.MobilePhone.extractPhoneValues(this.currCountry, this.countries, this.cellNumber);

                    this.cellNo = mobileExtracted.raw;
                    if (!mobileExtracted.prefix) {
                        const country: ILoyaltyAppCountryAssignmentModel = this.countries && this.countries .find(obj => obj.Code === this.currCountry);
                        if (country) {
                            this.countryId = country.Id;
                        }
                    } else {
                        this.countryId = mobileExtracted.countryPrefixId;
                    }
                }
            }

            this.formControl = this.formBuilder.group({
                name: [
                    { value: '', disabled: false },
                    [
                        Validators.required,
                        Validators.maxLength(50)
                    ]
                ],
                email: [
                    { value: '', disabled: false },
                    {
                        validators: Validators.compose([
                            Validators.required,
                            Validators.email
                        ]),
                        asyncValidators: Validators.composeAsync([
                            emailTakenValidator(this.membersService),
                        ]),
                    }
                ],
                countryPrefix: [
                    { value: this.countryId || (this.countries && this.countries.length && this.countries[0].Id) || '', disabled: false }
                ],
                cellNumber: [
                    { value: this.cellNo || '', disabled: false },
                    {
                        validators: Validators.compose(
                            cellPhoneCommonValidators(this.countries)
                        ),
                    },
                ],
                memberCard: [
                    { value: this.member && this.member.MemberCardNumber || null, disabled: true }
                ],
                password: [
                    { value: '', disabled: false },
                    [
                        Validators.required,
                        passwordPatternValidator(this.config.passwordRules),
                    ]
                ],
                receivePromo: [
                    { value: true, disabled: false }
                ]
            });

            if (this.cellNo) {
                this.formControl.controls.cellNumber.disable();
                this.formControl.controls.cellNumber.markAsDirty();
                this.formControl.controls.cellNumber.markAsTouched();

                if (this.countryId) {
                    this.formControl.controls.countryPrefix.disable();
                    this.formControl.controls.countryPrefix.markAsDirty();
                    this.formControl.controls.countryPrefix.markAsTouched();
                }
            }

            if (this.formName && this.detachFormSubmitControls && this.formsService) {
                console.warn(`Form submit controls are detached from form: ${this.formName}`);
                this.formsService.register(this.formName, this.formControl);
            }
        });
    }

    ngOnDestroy(): void {
        if (this.formName && this.detachFormSubmitControls && this.formsService) {
            this.formsService.unregister(this.formName);
        }
    }

    public handleFormSubmit($event: Event): void {
        const formValues = this.getFormValues();
        const country: ILoyaltyAppCountryAssignmentModel = this.countries && this.countries.find(c => c.Id === +formValues.countryPrefix);
        const phonePrefix = country && country.Id || 222;

        const rawModel: IMemberModel = {
            MemberName: formValues.name,
            Email: formValues.email,
            ReceivePromoAllowed: formValues.receivePromo,
            MobilePhone: formValues.cellNumber,
            CardNumber: formValues.memberCard,
            Password: formValues.password,
            MemberGroupId: this.config.defaultMemberGroupId || null,
            MobilePhoneCountryId: phonePrefix
        };

        this.controllers.members.signUpInit(rawModel);
    }

    public handleErrorButtonClick($event: Event): void {
        this.controllers.members.resetMemberDataFields();
        this.controllers.members.setAuthStep();
    }
}
